import { useAlertsStore } from '@/stores/alerts';
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import to from 'await-to-js';
import axios from 'axios';
import { defineStore } from 'pinia';
import { shallowRef } from 'vue';
import i18n from '@/plugins/vue-i18n';
import { get, size, uniqueId } from 'lodash';

const newlyAddedIdPrefix = 'newlyAddedIdPrefix';

export const useStoreExecutionsStore = defineStore('storeExecutions', () => {
  const scenarioStore = useScenariosStore();
  const snackbarStore = useSnackbarStore();
  const alertsStore = useAlertsStore();

  // State
  const loading = shallowRef(false);

  // Actions
  async function fetchCheckpointSelections() {
    const scenarioId = scenarioStore.selectedScenario._id;
    loading.value = true;
    const [err, response] = await to(
      axios.get(`/api/scenarios/${scenarioId}/store-executions/checkpoint-selections`)
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    return response.data.checkpointSelections;
  }

  async function saveCheckpointSelections(updates) {
    const scenarioId = scenarioStore.selectedScenario._id;
    loading.value = true;
    const [err] = await to(
      axios.post(`/api/scenarios/${scenarioId}/store-executions/checkpoint-selections`, { updates })
    );
    if (err) {
      loading.value = false;
      throw new Error(err.message);
    }
    await scenarioStore.loadScenario(scenarioId);
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
    loading.value = false;
  }

  async function fetchStoreAllocation() {
    const scenarioId = scenarioStore.selectedScenario._id;
    loading.value = true;
    const [err, response] = await to(
      axios.get(`/api/scenarios/${scenarioId}/store-executions/store-allocation`)
    );
    if (err) throw new Error(err.message);
    loading.value = false;
    return response.data;
  }

  async function saveStoreAllocation(payload) {
    const scenarioId = scenarioStore.selectedScenario._id;
    loading.value = true;
    const [err] = await to(
      axios.post(`/api/scenarios/${scenarioId}/store-executions/store-allocation`, payload)
    );
    if (err) throw new Error(err.message);
    await scenarioStore.loadScenario(scenarioId);
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
    loading.value = false;
  }

  async function fetchPlanogramDefinition() {
    const scenarioId = scenarioStore.selectedScenario._id;
    loading.value = true;
    const [err, response] = await to(
      axios.get(`/api/scenarios/${scenarioId}/store-executions/planogram-definition`)
    );
    if (err) throw new Error(err.message);
    loading.value = false;
    return response.data;
  }

  async function savePlanogramDefinition(payload) {
    const scenarioId = scenarioStore.selectedScenario._id;
    loading.value = true;
    const [err] = await to(
      axios.post(`/api/scenarios/${scenarioId}/store-executions/planogram-definition`, payload)
    );
    if (err) throw new Error(err.message);
    await scenarioStore.loadScenario(scenarioId);
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
    loading.value = false;
  }

  // storeExecutionsService is one of: store-allocation, planogram-definition
  async function processCSV({ fileId, mappings, storeExecutionsService, delimiter }) {
    const scenarioId = scenarioStore.selectedScenario._id;
    const body = { mappings, scenarioId, delimiter };
    loading.value = true;
    const [err, response] = await to(
      axios.post(`/api/csv/${storeExecutionsService}/process/${fileId}`, body)
    );
    loading.value = false;
    // TODO: Remove this and use global interceptor on AOV3-159
    if (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const message = i18n.t(key, get(err, 'response.data.messageParams', []));
      snackbarStore.showError(message);
      return Promise.reject(message);
    }
    if (size(response.data.messages)) {
      alertsStore.showMessages(response.data.messages);
    }
    const csvRows = response.data.tableRows;
    csvRows.forEach(row => {
      // add temp ID to uploaded entries
      row._id = row._id || uniqueId(`${newlyAddedIdPrefix}-${storeExecutionsService}`);
    });

    return response.data.tableRows;
  }

  return {
    // State
    loading,
    // Actions
    fetchCheckpointSelections,
    saveCheckpointSelections,
    fetchStoreAllocation,
    saveStoreAllocation,
    fetchPlanogramDefinition,
    savePlanogramDefinition,
    processCSV,
  };
});
